import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { authService } from "@/lib/auth-service";
import { API_BASE_URL } from "@/lib/consts";
import type { MagicLinkValidateResponse } from "@/types/strategy";

export const Route = createFileRoute("/strategy/magic-link")({
	validateSearch: (search: Record<string, unknown>) => ({
		token: (search.token as string) || "",
	}),
	component: MagicLinkPage,
});

function MagicLinkPage() {
	const { token } = Route.useSearch();
	const navigate = useNavigate();
	const [status, setStatus] = useState<"validating" | "success" | "error">(
		"validating",
	);
	const [errorMessage, setErrorMessage] = useState("");

	const validateToken = useCallback(
		async (magicToken: string) => {
			try {
				// Direct fetch (NOT apiService) since user is unauthenticated
				const response = await fetch(
					`${API_BASE_URL}/strategies/magic-link/validate`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ token: magicToken }),
					},
				);

				if (!response.ok) {
					const data = await response.json().catch(() => ({}));
					throw new Error(data.detail || "Invalid or expired magic link");
				}

				const data: MagicLinkValidateResponse = await response.json();

				// Store auth tokens
				authService.setTokens({
					authToken: data.access_token,
					refreshToken: data.refresh_token,
					expiry: 1800, // 30 min default
				});

				setStatus("success");

				// Brief delay to show success, then redirect
				setTimeout(() => {
					navigate({ to: "/strategy" });
				}, 1500);
			} catch (err) {
				setStatus("error");
				setErrorMessage(
					err instanceof Error ? err.message : "Failed to validate magic link",
				);
			}
		},
		[navigate],
	);

	useEffect(() => {
		if (!token) {
			setStatus("error");
			setErrorMessage("No magic link token provided.");
			return;
		}

		validateToken(token);
	}, [token, validateToken]);

	return (
		<div className="flex items-center justify-center min-h-screen bg-background">
			<Card className="max-w-md w-full mx-4">
				<CardContent className="pt-6 text-center space-y-4">
					{status === "validating" && (
						<>
							<Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
							<h2 className="text-xl font-semibold">Signing you in...</h2>
							<p className="text-muted-foreground">
								Please wait while we verify your magic link.
							</p>
						</>
					)}

					{status === "success" && (
						<>
							<CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
							<h2 className="text-xl font-semibold">You're signed in!</h2>
							<p className="text-muted-foreground">
								Redirecting to your strategy...
							</p>
						</>
					)}

					{status === "error" && (
						<>
							<AlertCircle className="h-10 w-10 text-destructive mx-auto" />
							<h2 className="text-xl font-semibold">Link expired or invalid</h2>
							<p className="text-muted-foreground">{errorMessage}</p>
							<p className="text-sm text-muted-foreground">
								Magic links expire after 24 hours. Please log in with your email
								and password instead.
							</p>
							<Link to="/login">
								<Button className="mt-2">Go to Login</Button>
							</Link>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
